import * as React from "react";
import { useParams } from "react-router";
import { useCurrentProject } from "@/lib/project";
import { useQuery } from "@tanstack/react-query";
import { Download, BarChart2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/bespoke";
import { formatDateTime } from "@/lib/format";

export type EvalRunSummary = {
  id: string;
  projectId: string;
  runId: string;
  criterionId: string | null;
  meanScore: number | null;
  passRate: number | null;
  itemCount: number;
  createdAt: string;
};

async function fetchSummaries(projectId: string, runId: string): Promise<EvalRunSummary[]> {
  const response = await fetch(`/api/v1/projects/${projectId}/eval/runs/${runId}/summaries`, {
    method: "GET",
    credentials: "same-origin",
  });
  if (!response.ok) return [];
  return (await response.json()).summaries;
}

async function fetchRun(projectId: string, runId: string) {
  const response = await fetch(`/api/v1/projects/${projectId}/eval/runs/${runId}`, {
    method: "GET",
    credentials: "same-origin",
  });
  if (!response.ok) return null;
  return response.json();
}

function SummaryRow({ summary }: { summary: EvalRunSummary }) {
  return (
    <tr className="group hover:bg-muted/50">
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 font-mono text-sm">
        {summary.criterionId ? summary.criterionId.slice(0, 8) + "…" : "Overall"}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 tabular-nums">
        {summary.meanScore !== null ? summary.meanScore.toFixed(3) : "—"}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 tabular-nums">
        {summary.passRate !== null ? (summary.passRate * 100).toFixed(1) + "%" : "—"}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 tabular-nums">
        {summary.itemCount}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 text-muted-foreground">
        {formatDateTime(summary.createdAt)}
      </td>
    </tr>
  );
}

export function EvalSummariesPage({ headingLevel = "h1" }: { headingLevel?: "h1" | "h2" }) {
  const { project } = useCurrentProject();
  const projectId = project?.id;
  const { runId } = useParams<{ runId: string }>();

  const { data: run, isLoading: runLoading } = useQuery({
    queryKey: ["eval-run", projectId, runId],
    queryFn: () => fetchRun(projectId!, runId!),
    enabled: !!projectId && !!runId,
  });

  const { data: summaries } = useQuery({
    queryKey: ["eval-summaries", projectId, runId],
    queryFn: () => fetchSummaries(projectId!, runId!),
    enabled: !!projectId && !!runId,
  });

  if (runLoading) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Loading..." headingLevel={headingLevel} />
      </div>
    );
  }

  const overallSummary = summaries?.find((s) => s.criterionId === null);
  const criterionSummaries = summaries?.filter((s) => s.criterionId !== null) ?? [];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={run?.name ? `${run.name} — Summary` : "Run Summary"}
        headingLevel={headingLevel}
        description={`Eval run: ${run?.environment ?? ""} — ${run?.trigger ?? ""}`}
        actions={
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
        }
      />

      <div className="grid gap-4 sm:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <dt className="text-xs font-medium text-muted-foreground">Overall score</dt>
            <dd className="mt-1 text-3xl font-semibold tabular-nums">
              {overallSummary?.meanScore ?? "—"}
            </dd>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <dt className="text-xs font-medium text-muted-foreground">Pass rate</dt>
            <dd className="mt-1 text-3xl font-semibold tabular-nums text-success">
              {overallSummary && overallSummary.passRate !== null
                ? (overallSummary.passRate * 100).toFixed(1) + "%"
                : "—"}
            </dd>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <dt className="text-xs font-medium text-muted-foreground">Items evaluated</dt>
            <dd className="mt-1 text-3xl font-semibold tabular-nums">
              {overallSummary?.itemCount ?? 0}
            </dd>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <dt className="text-xs font-medium text-muted-foreground">Criteria</dt>
            <dd className="mt-1 text-3xl font-semibold tabular-nums">
              {criterionSummaries.length}
            </dd>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>By criterion</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {criterionSummaries.length === 0 ? (
            <div className="p-8 text-center">
              <p className="font-medium">No criterion summaries yet</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                Summaries appear after the eval run completes and results are recorded.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <caption className="sr-only">Summaries by criterion</caption>
                <thead>
                  <tr className="border-b">
                    <th
                      scope="col"
                      className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[25%]"
                    >
                      Criterion
                    </th>
                    <th
                      scope="col"
                      className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[20%]"
                    >
                      <div className="flex items-center gap-1">
                        <BarChart2 className="h-3 w-3" />
                        Mean score
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[20%]"
                    >
                      <div className="flex items-center gap-1">
                        <BarChart2 className="h-3 w-3" />
                        Pass rate
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[15%]"
                    >
                      Items
                    </th>
                    <th
                      scope="col"
                      className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[20%]"
                    >
                      Computed
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {criterionSummaries.map((summary) => (
                    <SummaryRow key={summary.id} summary={summary} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
